import {
  definePreset,
  DEGREES_SCHEMA,
  DISTANCE_SCHEMA,
  kf,
  rad,
  setStatic,
  timing,
  toGsapEase,
} from "./helpers";

import type { AnimationPreset } from "./types";

export const attentionPresets: AnimationPreset[] = [
  definePreset(
    "confettiBurst",
    "Confetti",
    "attention",
    {
      params: { count: 24, spread: 1, gravity: 1 },
      schema: [
        { key: "count", label: "Pieces", min: 4, max: 80, step: 2 },
        { key: "spread", label: "Spread", min: 0.2, max: 3, step: 0.1 },
        { key: "gravity", label: "Gravity", min: 0.2, max: 3, step: 0.1 },
      ],
      duration: 1500,
      easing: "none", // ballistic arcs live in the particle math
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        emitKind: 0,
        emitCount: Math.round(cfg.params.count ?? 24),
        emitSpread: cfg.params.spread ?? 1,
        emitGravity: cfg.params.gravity ?? 1,
        emitSize: 1,
      });
      tl.fromTo(
        target,
        { emitProgress: 0 },
        { emitProgress: 1, ...timing(cfg), ease: "none" },
        0,
      );
    },
  ),
  definePreset(
    "sparkle",
    "Sparkle",
    "attention",
    {
      params: { count: 16, size: 1 },
      schema: [
        { key: "count", label: "Sparkles", min: 4, max: 80, step: 2 },
        { key: "size", label: "Size", min: 0.3, max: 3, step: 0.1 },
      ],
      duration: 2000,
      repeat: 2,
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        emitKind: 1,
        emitCount: Math.round(cfg.params.count ?? 16),
        emitSpread: 1,
        emitGravity: 0,
        emitSize: cfg.params.size ?? 1,
      });
      // the twinkle phase is periodic in the particle math, so a plain
      // linear progress loops seamlessly
      tl.fromTo(
        target,
        { emitProgress: 0 },
        { emitProgress: 1, ...timing(cfg), ease: "none" },
        0,
      );
    },
  ),
  definePreset(
    "boil",
    "Boil",
    "attention",
    {
      params: { fps: 3, strength: 0.5 },
      schema: [
        { key: "fps", label: "Frames/sec", min: 1, max: 12, step: 1 },
        { key: "strength", label: "Strength", min: 0, max: 2, step: 0.1 },
      ],
      duration: 1000,
      repeat: -1,
      easing: "none",
    },
    // the hand-drawn "boil": redraw the rough shape on a new seed a few
    // times a second ("on threes" by default). seedShift ramps linearly and
    // the renderer floors it, so exposures are exactly equal — a user ease
    // would warp the exposure lengths, hence the forced "none".
    // (No visible effect on freedraw/text: their bodies don't use roughjs.)
    (tl, target, _element, cfg) => {
      const frames = Math.max(
        1,
        Math.round((cfg.duration / 1000) * (cfg.params.fps ?? 3)),
      );
      setStatic(tl, target, {
        roughnessDelta: cfg.params.strength ?? 0.5,
      });
      tl.fromTo(
        target,
        { seedShift: 1 },
        {
          seedShift: frames + 1 - 1e-6,
          ...timing(cfg),
          ease: "none",
        },
        0,
      );
    },
  ),
  definePreset(
    "pulse",
    "Pulse",
    "attention",
    {
      params: { scale: 1.2 },
      schema: [{ key: "scale", label: "Scale", min: 1, max: 3, step: 0.05 }],
    },
    (tl, target, _element, cfg) => {
      // one pulse = scale up + back down => yoyo over half the duration.
      // gsap counts the yoyo return as a repeat, so N pulses need
      // (N * 2) - 1 repeats of the half-tween.
      const pulses = cfg.repeat === -1 ? -1 : cfg.repeat + 1;
      tl.to(
        target,
        {
          scaleX: cfg.params.scale ?? 1.2,
          scaleY: cfg.params.scale ?? 1.2,
          duration: cfg.duration / 2000,
          delay: cfg.delay / 1000,
          repeat: pulses === -1 ? -1 : pulses * 2 - 1,
          yoyo: true,
          ease: toGsapEase(cfg.easing),
        },
        0,
      );
    },
  ),
  definePreset(
    "rotate",
    "Spin",
    "attention",
    { params: { degrees: 360 }, schema: [DEGREES_SCHEMA] },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        { angleDelta: rad(cfg.params.degrees ?? 360), ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "bounce",
    "Bounce",
    "attention",
    {
      params: { height: 30 },
      schema: [{ key: "height", label: "Height", min: 5, max: 300, step: 5 }],
    },
    (tl, target, _element, cfg) => {
      const h = cfg.params.height ?? 30;
      tl.to(
        target,
        kf(cfg, { dy: [0, -h, 0, -h * 0.5, 0, -h * 0.2, 0] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "shake",
    "Shake",
    "attention",
    {
      params: { amplitude: 10 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 1, max: 100, step: 1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 10;
      tl.to(
        target,
        kf(cfg, { dx: [0, -a, a, -a, a, -a / 2, a / 2, 0] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "wobble",
    "Wobble",
    "attention",
    { params: { distance: 25 }, schema: [DISTANCE_SCHEMA] },
    (tl, target, _element, cfg) => {
      const d = cfg.params.distance ?? 25;
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: [0, -d, d * 0.8, -d * 0.6, d * 0.4, -d * 0.2, 0],
            angleDelta: [0, rad(-5), rad(3), rad(-3), rad(2), rad(-1), 0],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "swing",
    "Swing",
    "attention",
    {
      params: { degrees: 15, anchor: 0 },
      schema: [
        { key: "degrees", label: "Degrees", min: 1, max: 90, step: 1 },
        {
          key: "anchor",
          label: "Hang from",
          min: 0,
          max: 2,
          step: 1,
          options: ["Top", "Center", "Bottom"],
        },
      ],
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 15;
      // a swing hangs from an edge, not its center — animatable anchor
      setStatic(tl, target, {
        anchorY: [0, 0.5, 1][Math.round(cfg.params.anchor ?? 0)] ?? 0,
      });
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [
              0,
              rad(d),
              rad(-d * 0.66),
              rad(d * 0.33),
              rad(-d * 0.16),
              0,
            ],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "tada",
    "Tada",
    "attention",
    {
      params: { scale: 1.1, degrees: 3 },
      schema: [
        { key: "scale", label: "Scale", min: 1, max: 2, step: 0.05 },
        { key: "degrees", label: "Degrees", min: 1, max: 45, step: 1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const s = cfg.params.scale ?? 1.1;
      const down = Math.max(0, 2 - s);
      const d = cfg.params.degrees ?? 3;
      const scale = [1, down, down, s, s, s, s, s, s, 1];
      const angle = [0, -d, -d, d, -d, d, -d, d, -d, 0].map(rad);
      tl.to(
        target,
        kf(
          cfg,
          { scaleX: scale, scaleY: scale, angleDelta: angle },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "jello",
    "Jello",
    "attention",
    {
      params: { degrees: 12.5 },
      schema: [
        { key: "degrees", label: "Degrees", min: 1, max: 45, step: 0.5 },
      ],
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 12.5;
      const skew = [0, -1, 0.5, -0.25, 0.125, -0.0625, 0.03, 0].map((f) =>
        rad(f * d),
      );
      tl.to(target, kf(cfg, { skewX: skew, skewY: skew }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "flash",
    "Flash",
    "attention",
    {
      params: { flashes: 2, min: 0 },
      schema: [
        { key: "flashes", label: "Flashes", min: 1, max: 10, step: 1 },
        { key: "min", label: "Min opacity", min: 0, max: 0.9, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const flashes = Math.max(1, Math.round(cfg.params.flashes ?? 2));
      const min = cfg.params.min ?? 0;
      const opacity = [1];
      for (let i = 0; i < flashes; i++) {
        opacity.push(min, 1);
      }
      tl.to(target, kf(cfg, { opacity }, "none"), 0);
    },
  ),
  definePreset(
    "heartbeat",
    "Heartbeat",
    "attention",
    {
      params: { scale: 1.3 },
      schema: [{ key: "scale", label: "Scale", min: 1, max: 3, step: 0.05 }],
    },
    (tl, target, _element, cfg) => {
      const s = cfg.params.scale ?? 1.3;
      tl.to(
        target,
        kf(
          cfg,
          { scaleX: [1, s, 1, s, 1], scaleY: [1, s, 1, s, 1] },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "rubberBand",
    "Rubber Band",
    "attention",
    {
      params: { intensity: 1 },
      schema: [
        { key: "intensity", label: "Intensity", min: 0.1, max: 3, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const k = cfg.params.intensity ?? 1;
      const scaled = (values: number[]) => values.map((v) => 1 + (v - 1) * k);
      tl.to(
        target,
        kf(
          cfg,
          {
            scaleX: scaled([1, 1.25, 0.75, 1.15, 0.95, 1.05, 1]),
            scaleY: scaled([1, 0.75, 1.25, 0.85, 1.05, 0.95, 1]),
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "wiggle",
    "Wiggle",
    "attention",
    {
      params: { degrees: 3 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 30, step: 1 }],
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 3;
      tl.to(
        target,
        kf(
          cfg,
          { angleDelta: [0, -d, d, -d, d, -d, d, 0].map(rad) },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
  definePreset(
    "headShake",
    "Head Shake",
    "attention",
    {
      params: { amplitude: 8 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 2, max: 60, step: 1 },
      ],
      duration: 800,
      easing: "power1.inOut",
    },
    // the animate.css "no" gesture: horizontal shake with a coupled tilt,
    // both decaying to rest (distinct from `shake`, which is dx only)
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 8;
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: [0, -a, a * 0.7, -a * 0.4, a * 0.2, 0],
            angleDelta: [0, -6, 4, -3, 1.5, 0].map((deg) => rad((deg * a) / 8)),
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
];
